import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import AppLogo from "../../assets/images/logo.png";
import { HOME_ROUTE } from "../../routes/rootRoutes";
import { ADDRESS, CONTACT, EMAIL, LOGO_LABEL, LOGO_SLOGAN } from "../../utils/constant";
import {
  socialLinks,
  footerSections,
  contactInfo,
  legalLinks,
  schoolInfo,
} from "../../data/footerData";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const processedContactInfo = contactInfo.map((item) => {
    let text = item.text;
    let url = item.url;
    
    if (item.text.includes("ADDRESS_PLACEHOLDER")) {
      text = ADDRESS;
    }
    if (item.text.includes("PHONE_PLACEHOLDER")) {
      text = item.text.replace("PHONE_PLACEHOLDER", CONTACT);
    }
    if (item.text.includes("CONTACT_PLACEHOLDER")) {
      text = item.text.replace("CONTACT_PLACEHOLDER", CONTACT);
    }
    if (item.text.includes("EMAIL_PLACEHOLDER")) {
      text = EMAIL;
    }
    
    if (item.url && item.url.includes("CONTACT_PLACEHOLDER")) {
      url = item.url.replace("CONTACT_PLACEHOLDER", CONTACT);
    }
    if (item.url && item.url.includes("EMAIL_PLACEHOLDER")) {
      url = item.url.replace("EMAIL_PLACEHOLDER", EMAIL);
    }
    
    return {
      ...item,
      text,
      url,
    };
  });

  const renderLink = (link) => {
    if (link.external) {
      return (
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-gray-400 transition-all duration-300 hover:text-blue-400 group"
        >
          <span className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-300"></span>
          <span className="transition-transform duration-300 group-hover:translate-x-1">
            {link.name}
          </span>
          <span className="ml-2 text-xs transition-opacity opacity-0 group-hover:opacity-100">
            ↗
          </span>
        </a>
      );
    }

    return (
      <Link
        to={link.url}
        className="flex items-center text-gray-400 transition-all duration-300 hover:text-blue-400 group"
      >
        <span className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-300"></span>
        <span className="transition-transform duration-300 group-hover:translate-x-1">
          {link.name}
        </span>
      </Link>
    );
  };

  const renderContactItem = (item) => {
    const content = (
      <motion.div
        className="flex items-start space-x-3 cursor-pointer group"
        whileHover={{ x: 2 }}
      >
        <div className="flex-shrink-0 mt-1 text-blue-400 transition-colors duration-200 group-hover:text-blue-300">
          {item.icon}
        </div>
        <span className="text-gray-300 transition-colors duration-200 group-hover:text-white">
          {item.text}
        </span>
      </motion.div>
    );

    if (item.url) {
      const linkProps = item.external 
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {};
      
      return (
        <a
          key={item.text}
          href={item.url}
          className="block"
          {...linkProps}
        >
          {content}
        </a>
      );
    }

    return <div key={item.text}>{content}</div>;
  };

  return (
    <footer className="text-gray-300 bg-gray-900 border-t border-gray-800">
      {/* Main Footer Content */}
      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-4">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* School Branding */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <div className="flex items-center">
              <Link
                to={HOME_ROUTE}
                className="flex items-center space-x-3 md:space-x-4 group"
              >
                <img
                  src={AppLogo}
                  className="relative w-auto h-10 transition duration-300 transform md:h-12 lg:h-24 group-hover:scale-105"
                  alt="logo"
                />
                <div className="block">
                  <h1 className="text-sm font-bold text-transparent transition-all duration-300 md:text-xl lg:text-lg bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text group-hover:from-blue-300 group-hover:to-purple-300">
                    {LOGO_LABEL}
                  </h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-blue-300 font-semibold text-[10px] md:text-sm tracking-wide group-hover:text-blue-200 transition-colors duration-300">
                      {LOGO_SLOGAN}
                    </p>
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse group-hover:bg-green-300 transition-colors duration-300" />
                  </div>
                </div>
              </Link>
            </div>

            <p className="pt-2 text-sm leading-relaxed text-gray-400">
              {schoolInfo.name} | {schoolInfo.description}
            </p>
            <p className="text-xs text-gray-500">
              {schoolInfo.accreditation} • Est. {schoolInfo.foundedYear}
            </p>
            <p className="text-xs italic text-gray-500">
              "{schoolInfo.motto}"
            </p>

            <div className="flex pt-2 space-x-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  whileHover={{ scale: 1.15, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative group"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                >
                  <div className="absolute transition-opacity duration-300 rounded-full opacity-0 -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 blur group-hover:opacity-40"></div>
                  <div className="relative flex items-center justify-center w-10 h-10 text-gray-400 transition-all duration-300 bg-gray-800 border border-gray-700 rounded-full group-hover:border-blue-500 group-hover:text-white">
                    {social.icon}
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Footer Sections */}
          {Object.entries(footerSections).map(([key, section], sectionIndex) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * (sectionIndex + 1) }}
            >
              <h3 className="flex items-center mb-4 text-sm font-semibold tracking-wider text-gray-200 uppercase">
                <span className="mr-2 text-blue-500">{section.icon}</span>
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, index) => (
                  <motion.li
                    key={index}
                    whileHover={{ x: 5 }}
                    className="text-sm"
                  >
                    {renderLink(link)}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="space-y-6"
          >
            <h3 className="mb-2 text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text">
              Contact Us
            </h3>

            <div className="space-y-4 text-xs">
              {processedContactInfo.map(renderContactItem)}
            </div>

            <div className="pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-500">
                Need immediate assistance? 
                <a 
                  href={`tel:+91${CONTACT}`}
                  className="ml-2 text-blue-400 transition-colors hover:text-blue-300"
                >
                  Call us now
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-gray-800 border-t border-gray-700">
        <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between md:flex-row">
            <div className="flex items-center mb-4 text-xs text-gray-500 md:mb-0">
              © {currentYear} {schoolInfo.name}. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-xs">
              {legalLinks.map((item, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -1 }}
                >
                  <Link
                    to={item.url}
                    className="text-gray-500 transition-colors duration-200 hover:text-blue-400"
                  >
                    {item.label}
                  </Link>
                  {index < legalLinks.length - 1 && (
                    <span className="ml-4 text-gray-600">•</span>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;